<script setup>
import { computed } from 'vue';

import Icon from 'next/icon/Icon.vue';
import MessageMeta from '../MessageMeta.vue';
import CaptainGenerationDetails from '../CaptainGenerationDetails.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION, SENDER_TYPES } from '../constants';
import {
  parseGroupSender,
  stripMediaPlaceholder,
  colorForName,
} from 'dashboard/composables/useGroupSender';
import { useGroupMentions } from 'dashboard/composables/useGroupMentions';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  id,
  sender,
  senderType,
  groupSender,
  groupWithPrevious,
  contentAttributes,
} = useMessageContext();

const isForwarded = computed(() => !!contentAttributes.value?.forwarded);

// Only the first bubble of a participant's run shows the heading, the way
// WhatsApp Web collapses consecutive messages from the same person.
const shouldShowGroupSender = computed(
  () => groupSender.value && !groupWithPrevious.value
);
const { t } = useI18n();
const highlightMentions = useGroupMentions();

const isCaptainMessage = computed(
  () =>
    (sender.value?.type ?? senderType.value) === SENDER_TYPES.CAPTAIN_ASSISTANT
);

const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);

const emailMetaClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : ''
);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

// The quoted message carries the same gateway prefix and media labels as a
// regular one, so it is parsed the same way: the participant becomes a
// coloured heading and the placeholder label gives way to the media name.
const replyToSender = computed(() => {
  if (!inReplyTo.value) return null;

  const parsed = parseGroupSender(inReplyTo.value.content);
  if (!parsed) return null;

  return { name: parsed.name, colorClass: colorForName(parsed.name) };
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;
  const hasAttachments = attachments?.length > 0;
  const body = stripMediaPlaceholder(
    parseGroupSender(content)?.body ?? content,
    hasAttachments
  );

  if (body) {
    return highlightMentions(new MessageFormatter(body).formattedMessage);
  }
  if (hasAttachments) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="flex gap-2 p-2 -mx-1 mb-2 overflow-hidden rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-if="replyToSender"
        class="flex-shrink-0 w-1 -my-2 ltr:-ml-2 rtl:-mr-2 rounded-full bg-current"
        :class="replyToSender.colorClass"
      />
      <div class="min-w-0">
        <div
          v-if="replyToSender"
          class="text-sm font-semibold truncate"
          :class="replyToSender.colorClass"
        >
          {{ replyToSender.name }}
        </div>
        <div
          v-dompurify-html="replyToPreview"
          class="prose prose-bubble line-clamp-2"
        />
      </div>
    </div>
    <div
      v-if="shouldShowGroupSender"
      class="mb-1 text-sm font-semibold truncate"
      :class="groupSender.colorClass"
    >
      {{ groupSender.name }}
    </div>
    <div
      v-if="isForwarded"
      class="flex items-center gap-1 mb-1 text-xs italic"
      :class="metaColorClass"
    >
      <Icon icon="i-lucide-forward" class="size-3 flex-shrink-0" />
      <span class="truncate">{{ t('CONVERSATION.FORWARDED') }}</span>
    </div>
    <slot />
    <template v-if="shouldShowMeta">
      <CaptainGenerationDetails
        v-if="isCaptainMessage"
        :message-id="id"
        class="mt-2"
      >
        <template #meta>
          <MessageMeta :class="[emailMetaClass, metaColorClass]" />
        </template>
      </CaptainGenerationDetails>
      <MessageMeta
        v-else
        :class="[flexOrientationClass, emailMetaClass, metaColorClass]"
        class="mt-2"
      />
    </template>
  </div>
</template>
